import {
  Typography,
  Avatar,
  Button,
  Input,
  IconButton,
} from "@material-tailwind/react";
import {
  PencilSquareIcon,
  TrashIcon,
  XCircleIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  EyeIcon,
  PlusCircleIcon
} from "@heroicons/react/24/solid";
import Swal from "sweetalert2";
import { Popover } from 'react-tiny-popover';
import ShowLoader from "@/components/Loader";
import Toaster from "../../components/Toaster";
import Pagination from "../../components/Pagination";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useHasPermission from "../../components/useHasPermission";
import { getBanners, deleteBanner, changeBannerStatus, setSelectedBanner } from "@/store/slices/bannerSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

export function Banner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { t } = useTranslation("banner");
  const { t: ct } = useTranslation("common");
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const hasAddPermission = useHasPermission('banner.add');
  const hasViewPermission = useHasPermission('banner.view');
  const hasUpdatePermission = useHasPermission('banner.update');
  const hasDeletePermission = useHasPermission('banner.delete');

  const filterOptions = [
    {
      label: t('statusOptions.active'),
      value: 'active'
    },
    {
      label: t('statusOptions.inactive'),
      value: 'inactive'
    }
  ];

  const { list_banner, loading, list_loading } = useSelector((state) => state.banner);

  useEffect(() => {
    dispatch(getBanners({ page: currentPage, search: search, filter: filter, perPage: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  const handelSearch = (event) => {
    const value = event.target.value;
    if (event.key === "Enter" && value) {
      dispatch(getBanners({ page: 1, search: search, filter: filter, perPage: itemsPerPage }));
    } else if (event.key === "Backspace" && value.length === 0) {
      dispatch(getBanners({ page: 1, search: '', filter: filter, perPage: itemsPerPage }));
    }
  }

  const handleSearchClick = () => {
    dispatch(getBanners({ page: 1, search: search, filter: filter, perPage: itemsPerPage }));
  }

  const handelRemoveSearch = () => {
    setSearch('');
    dispatch(getBanners({ page: 1, search: '', filter: filter, perPage: itemsPerPage }));
  }

  const handelFilter = (value) => {
    setFilter(value);
    dispatch(getBanners({ page: 1, search: search, filter: value, perPage: itemsPerPage }));
  }

  const handelEdit = (id, type) => {
    if (id) {
      const banner = list_banner?.banners?.find((row) => String(row.id) === String(id));
      dispatch(setSelectedBanner(banner));
      navigate(`/dashboard/banners/${type}/${id}`);
    }
  }

  const delete_row = (id) => {
    Swal.fire({
      title: t('deleteDialog.title'),
      text: t('deleteDialog.text'),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('deleteDialog.confirmButton'),
      cancelButtonText: ct("cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteBanner(id)).unwrap().then(() => {
          Toaster.success(t('deleteDialog.deleteSuccess'));
          dispatch(getBanners({ page: currentPage, search: search, filter: filter, perPage: itemsPerPage }));
        }).catch((error) => {
          console.error("Delete failed:", error);
          Toaster.error(t('deleteDialog.deleteFailed'));
        });
      }
    });
  }

  const handleChangeStatus = async (id, value) => {
    let text = value === 'active' ? t('statusChangeDialog.activateText') : t('statusChangeDialog.inactivateText');
    Swal.fire({
      title: t('statusChangeDialog.title'),
      text: `${text}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('statusChangeDialog.confirmButton'),
      cancelButtonText: ct("cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        let msg = value === 'active' ? `${ct('activated')}` : `${ct('deactivated')}`;
        dispatch(changeBannerStatus({ id, status: value })).unwrap().then(() => {
          Toaster.success(t("statusChangeSuccessMessage", { status: msg }));
          dispatch(getBanners({ page: currentPage, search: search, filter: filter, perPage: itemsPerPage }));
        }).catch((error) => {
          console.error("Changes failed:", error);
          Toaster.error(t('statusChangeDialog.changeFailed'));
        });
      }
    });
  };

  const updateBannerPriority = (id, newPriority) => {
    console.log("Banner ID :", id, "Priority : ", newPriority);
  }

  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);

  const [isPopoverOpen, setIsPopoverOpen] = useState([]);
  const openActionPopover = (id) => {
    setIsPopoverOpen((prev) => {
      if (prev.includes(id)) {
        return [];
      } else {
        return [id];
      }
    });
  };

  const popoverRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsPopoverOpen([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
        <div className="w-full md:flex-1">
          <Typography variant="h5" color="blue-gray">
            {t('banners')}
          </Typography>
        </div>
        <div className="w-full md:flex-1 flex justify-center">
          <div className="w-full max-w-md relative">
            <Input className={search ? 'pr-10' : ''} label={ct('search')} icon={!search ? <MagnifyingGlassIcon className="h-5 w-5" /> : ''}  value={search} onChange={(e) => setSearch(e.target.value)} onKeyUp={handelSearch} />
            {search && (
              <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                <XCircleIcon className="h-5 w-5 text-red-500" />
              </IconButton>
            )}
        </div>
        {search && ( <Button variant="outlined" className="flex items-center border-gray-300 capitalize font-bold bg-black text-white mx-2"
          onClick={handleSearchClick}  
        ><MagnifyingGlassIcon className="w-4 h-4 mr-1" /> {t('search')}</Button> )}
        </div>
        {hasAddPermission && <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 md:px-6">
          <Link to={`/dashboard/banners/add`}>
            <Button variant="outlined" className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"><PlusCircleIcon className="w-4 h-4" /> {t('addBannerButton')}</Button>
          </Link>
        </div>}
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
        <div className="w-full md:flex-1">
          <Typography variant="h6" color="blue-gray">
            {t('items')} ({list_banner?.pagination?.total || 0})
          </Typography>
        </div>
        <div className="flex w-full md:w-auto items-center gap-3 flex-wrap justify-end md:justify-end">
          <Popover
            isOpen={isFilterPopoverOpen}
            positions={["right", "bottom", "left"]}
            padding={4}
            onClickOutside={() => setIsFilterPopoverOpen(false)}
            content={
              <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                <button
                  onClick={() => {
                    handelFilter("");
                    setIsFilterPopoverOpen(false);
                  }}
                  className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === '' ? 'bg-gray-100 font-semibold' : ''}`}
                >
                  {t('all')}
                </button>
                {filterOptions?.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handelFilter(option.value);
                      setIsFilterPopoverOpen(false);
                    }}
                    className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === option.value ? 'bg-gray-100 font-semibold' : ''}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            }
          >
            <Button
              onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
              variant="outlined"
              className="flex items-center gap-2 border-gray-300 text-gray-800 capitalize font-medium"
            >
              <FunnelIcon className="h-4 w-4" /> 
              {/* {t('filter')} {filter && `(${filter})`} */}
              {t('filter')} {filter && `(${filterOptions.find(option => option.value === filter)?.label})`}
            </Button>
          </Popover>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] table-auto text-left">
          <tbody>
            {list_loading && <tr><td colSpan={6} className="text-center"> <div role="status">
              <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
              </svg>
            </div></td></tr>}

            {list_banner?.banners?.length > 0 ? (
              list_banner.banners.map(({ id, image, title, sub_title, description, status, priority }, key) => {

                return (
                  <tr key={id} className="align-top">
                    <td className="py-3 px-5">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
                        <Avatar 
                          src={image || '/img/placeholder-banner.png'}
                          alt={title} 
                          size="md" 
                          variant="circular" 
                          className="w-full h-full object-cover object-center" 
                        />
                      </div>
                    </td>
                    
                    <td className="py-3 px-5 align-middle max-w-xs">
                      <Typography color="blue-gray" className="font-semibold text-sm">{title}</Typography>
                    </td>

                    <td className="py-3 px-5 align-middle max-w-xs">
                      <Typography className="text-xs font-normal text-blue-gray-500">{sub_title ?? '_'}</Typography>
                    </td>
                    
                    <td className='py-3 px-5 align-middle max-w-xs'>
                      <Typography className="text-xs font-normal text-blue-gray-600 truncate">{description}</Typography>
                    </td>
                    
                    {/* <td className="py-3 px-5 align-middle">
                      <BannerPriorityInput initialPriority={priority} onUpdate={(newPriority) => updateBannerPriority(id, newPriority)} />
                    </td> */}
                    
                    <td className="py-3 px-5 align-middle">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${status == 'inactive' ? 'bg-yellow-50' : 'bg-green-50'}`}>
                            {status === 'active' ? ct("active"): ct("inactive") }
                        </span>
                    </td>

                    <td className="py-3 px-5 align-middle">
                    <div className="flex items-center space-x-2">
                      <Popover
                          isOpen={isPopoverOpen.includes(id)}
                          positions={['bottom', 'left']}
                          padding={4}
                          content={<div ref={popoverRef} className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-100 py-1">
                            {hasViewPermission &&
                              <button
                                className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                onClick={() => handelEdit(id, 'view')}
                              >
                                <EyeIcon className="h-4 w-4 text-blue-500"></EyeIcon>
                                {t('actions.view')}
                              </button>
                            }
                            {hasUpdatePermission &&
                              <button
                                className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                onClick={() => handelEdit(id, 'edit')}
                              >
                                <PencilSquareIcon className="h-4 w-4 text-blue-500"></PencilSquareIcon>
                                {t('actions.edit')}
                              </button>
                            }
                            {hasDeletePermission &&
                              <button
                                className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                onClick={() => delete_row(id)}
                              >
                                <TrashIcon className="h-4 w-4 text-red-500"></TrashIcon>
                                {t('actions.delete')}
                              </button>
                            }
                            {hasUpdatePermission && (
                              <>
                                {status === "active" &&
                                  <button
                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                    onClick={() => handleChangeStatus(id, "inactive")}
                                  >
                                    <XCircleIcon className="h-4 w-4 text-red-500" />
                                    {t('actions.inactivate')}
                                  </button>
                                }
                                {status === "inactive" &&
                                  <button
                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                    onClick={() => handleChangeStatus(id, "active")}
                                  >
                                    <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                    {t('actions.activate')}
                                  </button>
                                }
                              </>
                            )}
                          </div>}>
                          <IconButton variant="text" color="blue-gray" onClick={() => openActionPopover(id)}>
                            <EllipsisVerticalIcon className="h-6 w-6" />
                          </IconButton>
                        </Popover>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              !list_loading && (
                <tr><td colSpan={6} className="text-center py-10 text-blue-gray-500">{t('noDataFound')}</td></tr>
              )
            )}
          </tbody>
        </table>
      </div>
      {list_banner?.pagination && list_banner?.pagination?.total > 0 && (
        <Pagination currentPage={currentPage} itemsPerPage={itemsPerPage} setCurrentPage={setCurrentPage} setItemsPerPage={setItemsPerPage} totalPages={list_banner?.pagination?.last_pages} />
      )}
    </div>
  );
}

export default Banner;